import { useCallback, useMemo, KeyboardEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dialogActions, NAVIGATION_LEVELS } from '../reducers/dialogSlice';
import { selectDialog } from '../selectors/dialog';
import { CartItem } from '../utils/cart';
import { ParsedModifierGroup } from '../types/menu';
import { sortModGroups } from '../utils/menu';
import { getNestedModGroupsWithPathsForSelectedModGroup } from '../utils/menu';

// WASD navigation key constants
const WASD_KEYS = {
  W: 'W',
  A: 'A',
  S: 'S',
  D: 'D',
} as const;

interface UseModifierGroupNavigationProps {
  selectedCartItem?: CartItem;
}

export const useModifierGroupNavigation = ({
  selectedCartItem,
}: UseModifierGroupNavigationProps) => {
  const dispatch = useDispatch();
  const dialogState = useSelector(selectDialog);
  const {
    nextSelectedModGroupIndex,
    selectedModGroup,
    navigationLevel,
    parentModGroupId,
    clickedModifier,
  } = dialogState;

  // Memoize the expensive nested modifier groups computation for current navigation level
  const nestedModGroupsWithPaths = useMemo(() => {
    if (
      selectedCartItem &&
      parentModGroupId &&
      navigationLevel === NAVIGATION_LEVELS.NESTED
    ) {
      return getNestedModGroupsWithPathsForSelectedModGroup(
        selectedCartItem,
        parentModGroupId
      );
    }
    return [];
  }, [selectedCartItem, parentModGroupId, navigationLevel]);

  // Memoize nested modifier groups for going into nested level (Shift+D)
  const nestedModGroupsForDrillDown = useMemo(() => {
    if (
      selectedCartItem &&
      selectedModGroup &&
      navigationLevel === NAVIGATION_LEVELS.TOP
    ) {
      return getNestedModGroupsWithPathsForSelectedModGroup(
        selectedCartItem,
        selectedModGroup.id
      );
    }
    return [];
  }, [selectedCartItem, selectedModGroup, navigationLevel]);

  // Helper function to get modifier groups to navigate based on navigation level
  const getModifierGroupsToNavigate = useCallback((): ParsedModifierGroup[] => {
    if (!selectedCartItem) return [];

    if (navigationLevel === NAVIGATION_LEVELS.TOP) {
      return sortModGroups(selectedCartItem);
    } else if (
      navigationLevel === NAVIGATION_LEVELS.NESTED &&
      parentModGroupId
    ) {
      return nestedModGroupsWithPaths.map((item) => item.modGroup);
    }

    return [];
  }, [
    selectedCartItem,
    navigationLevel,
    parentModGroupId,
    nestedModGroupsWithPaths,
  ]);

  // Helper function to get path to item for a selected modifier group
  const getPathToItem = useCallback(
    (modGroup: ParsedModifierGroup): string => {
      if (navigationLevel === NAVIGATION_LEVELS.TOP) {
        return modGroup.id;
      } else if (selectedCartItem && parentModGroupId) {
        const selectedModGroupWithPath = nestedModGroupsWithPaths.find(
          (item) => item.modGroup.id === modGroup.id
        );
        return selectedModGroupWithPath?.pathToItem || modGroup.id;
      }
      return modGroup.id;
    },
    [
      navigationLevel,
      selectedCartItem,
      parentModGroupId,
      nestedModGroupsWithPaths,
    ]
  );

  // Helper function to calculate next index based on direction
  const calculateNextIndex = useCallback(
    (
      currentIndex: number,
      totalLength: number,
      direction: 'forward' | 'backward'
    ): number => {
      if (direction === 'forward') {
        return currentIndex < totalLength - 1 ? currentIndex + 1 : 0;
      } else {
        return currentIndex > 0 ? currentIndex - 1 : totalLength - 1;
      }
    },
    []
  );

  // Navigate through modifier groups in the specified direction
  const navigateModifierGroups = useCallback(
    (event: KeyboardEvent<Element>, direction: 'forward' | 'backward') => {
      event.preventDefault();
      event.stopPropagation();

      const modGroupsToNavigate = getModifierGroupsToNavigate();
      if (modGroupsToNavigate.length) {
        const currentIndex = nextSelectedModGroupIndex - 1;
        const newIndex = calculateNextIndex(
          currentIndex,
          modGroupsToNavigate.length,
          direction
        );

        const selectedModGroup = modGroupsToNavigate[newIndex];
        const pathToItem = getPathToItem(selectedModGroup);

        dispatch(
          dialogActions.updateSelectedModGroup({
            modGroup: selectedModGroup,
            pathToItem: pathToItem,
          })
        );
        dispatch(dialogActions.setSelectedModGroupIndex(newIndex + 1));

        // Reset clickedModifier when using WASD navigation (only if it exists)
        if (clickedModifier) {
          dispatch(dialogActions.setClickedModifier(undefined));
        }
      }
    },
    [
      getModifierGroupsToNavigate,
      nextSelectedModGroupIndex,
      calculateNextIndex,
      getPathToItem,
      clickedModifier,
      dispatch,
    ]
  );

  // Navigate through modifier groups (forward with Shift+S, backward with Shift+W)
  const handleNavigateModifierGroups = useCallback(
    (event: KeyboardEvent<Element>, direction: 'forward' | 'backward') => {
      navigateModifierGroups(event, direction);
    },
    [navigateModifierGroups]
  );

  // Navigate deeper into nested modifier groups (using Shift+D)
  const handleNavigateToNestedLevel = useCallback(
    (event: KeyboardEvent<Element>) => {
      event.preventDefault();
      event.stopPropagation();

      if (nestedModGroupsForDrillDown.length > 0) {
        // Automatically select the first nested modifier group
        const firstNestedModGroupWithPath = nestedModGroupsForDrillDown[0];
        dispatch(
          dialogActions.navigateToNestedLevel({
            parentModGroupId: selectedModGroup!.id,
            firstNestedModGroup: firstNestedModGroupWithPath.modGroup,
            firstNestedModGroupPath: firstNestedModGroupWithPath.pathToItem,
          })
        );
        // Reset clickedModifier when using WASD navigation (only if it exists)
        if (clickedModifier) {
          dispatch(dialogActions.setClickedModifier(undefined));
        }
      }
    },
    [nestedModGroupsForDrillDown, selectedModGroup, clickedModifier, dispatch]
  );

  // Navigate back to parent level (using Shift+A)
  const handleNavigateToParentLevel = useCallback(
    (event: KeyboardEvent<Element>) => {
      event.preventDefault();
      event.stopPropagation();
      if (
        navigationLevel === NAVIGATION_LEVELS.NESTED &&
        parentModGroupId &&
        selectedCartItem
      ) {
        // Find the parent modifier group to restore selection
        const topLevelModGroups = sortModGroups(selectedCartItem);
        const parentModGroup = topLevelModGroups.find(
          (modGroup) => modGroup.id === parentModGroupId
        );

        dispatch(
          dialogActions.navigateToParentLevel({
            parentModGroup: parentModGroup,
            parentModGroupPath: parentModGroup?.id,
          })
        );
        // Reset clickedModifier when using WASD navigation (only if it exists)
        if (clickedModifier) {
          dispatch(dialogActions.setClickedModifier(undefined));
        }
      }
    },
    [
      navigationLevel,
      parentModGroupId,
      selectedCartItem,
      clickedModifier,
      dispatch,
    ]
  );

  // Handle WASD navigation key events
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<Element>) => {
      // WASD navigation with Shift modifiers
      if (!event.shiftKey) return;

      // Handle different navigation keys
      switch (event.key) {
        case WASD_KEYS.S: // Shift+S: Move down (forward) through modifier groups
          handleNavigateModifierGroups(event, 'forward');
          break;
        case WASD_KEYS.W: // Shift+W: Move up (backward) through modifier groups
          handleNavigateModifierGroups(event, 'backward');
          break;
        case WASD_KEYS.D: // Shift+D: Go to nested level
          handleNavigateToNestedLevel(event);
          break;
        case WASD_KEYS.A: // Shift+A: Go one level up
          handleNavigateToParentLevel(event);
          break;
        default:
          return;
      }
    },
    [
      handleNavigateModifierGroups,
      handleNavigateToNestedLevel,
      handleNavigateToParentLevel,
    ]
  );

  return {
    handleKeyDown,
    handleNavigateModifierGroups,
    handleNavigateToNestedLevel,
    handleNavigateToParentLevel,
  };
};

export default useModifierGroupNavigation;
